/**
 * Axes, Winning squares, Grid settings and "When a new grid is created"
 * (screens 05, 12). One set of fields for the create step and the Settings
 * tab. Squares on grid, axes and checkpoints freeze at the season's first
 * lock; the server names the frozen fields and this only disables them.
 */
import { Input, Select, Switch } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { SquaresChipGroup } from "./SquaresChipGroup";
import {
  squaresAutoLockDescription,
  squaresAutoLockLabel,
  SQUARES_AXES_CUSTOM_HINT,
  SQUARES_AXES_CUSTOM_SIDE,
  SQUARES_AXES_CUSTOM_TOP,
  SQUARES_AXES_TEAM_PICKED,
  SQUARES_CARRY_SQUARES_DESCRIPTION,
  SQUARES_CARRY_SQUARES_LABEL,
  SQUARES_GRID_SIZE_HINT_SMALL,
  SQUARES_GRID_SIZE_LABEL,
  SQUARES_GRID_SIZE_OPTION,
  SQUARES_MAX_PER_MEMBER_ERROR,
  SQUARES_MAX_PER_MEMBER_HINT,
  SQUARES_MAX_PER_MEMBER_LABEL,
  SQUARES_MAX_PER_MEMBER_UNLIMITED,
  SQUARES_REDRAW_NUMBERS_DESCRIPTION,
  SQUARES_REDRAW_NUMBERS_LABEL,
  SQUARES_AUTO_PUBLISH_DESCRIPTION,
  SQUARES_AUTO_PUBLISH_LABEL,
  SQUARES_SETTINGS_LOCKED_AFTER_FIRST_LOCK,
  SQUARES_SETUP_AXES_HEADING,
  SQUARES_SETUP_AXES_HINT,
  SQUARES_SETUP_CHECKPOINTS_HEADING,
  SQUARES_SETUP_CHECKPOINTS_HINT,
  SQUARES_SETUP_GRID_SETTINGS_HEADING,
  SQUARES_SETUP_NEW_GRID_HEADING,
  SQUARES_SHOW_NUMBERS_DESCRIPTION,
  SQUARES_SHOW_NUMBERS_LABEL,
} from "./squaresCopy";
import type {
  typeSquaresAxes,
  typeSquaresAxesKind,
  typeSquaresGridSettings,
  typeSquaresGridType,
  typeSquaresNewGridRules,
  typeSquaresPresetsData,
} from "./squaresTypes";
import type { typeSquaresTeam } from "./useSquaresLeagueTeams";

export type typeSquaresGridRulesValue = {
  gridType: typeSquaresGridType;
  axes: typeSquaresAxes | null;
  checkpoints: readonly string[];
  gridSettings: typeSquaresGridSettings;
  newGrid: typeSquaresNewGridRules;
};

export type typeSquaresGridRulesFieldsProps = {
  presets: typeSquaresPresetsData;
  value: typeSquaresGridRulesValue;
  onChangeAxes: (axes: typeSquaresAxes) => void;
  onChangeCheckpoints: (checkpoints: readonly string[]) => void;
  onChangeGridSettings: (gridSettings: typeSquaresGridSettings) => void;
  onChangeNewGrid: (newGrid: typeSquaresNewGridRules) => void;
  /** Server field names frozen at the first lock: "size", "axes", "checkpoints". */
  frozenFields?: readonly string[] | undefined;
  isNewGridShown: boolean;
  /** "First lock: Sun, Oct 20 · 1:00 PM", under the auto-lock switch. */
  autoLockNote?: string | null | undefined;
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
  /** False drops Custom, whose two labels are typed only at creation. */
  isCustomAxesOffered?: boolean | undefined;
};

const FrozenNote = (): ReactNode => (
  <p
    className="text-caption text-ink-secondary"
    data-testid="squares-frozen-note"
  >
    {SQUARES_SETTINGS_LOCKED_AFTER_FIRST_LOCK}
  </p>
);

/** Empty is unlimited; anything else must be a whole number of 1 or more. */
export function parseSquaresMaxPerMember(
  text: string,
): { ok: true; value: number | null } | { ok: false } {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: true, value: null };
  if (!/^\d+$/.test(trimmed)) return { ok: false };
  const value = Number(trimmed);
  return value >= 1 ? { ok: true, value } : { ok: false };
}

const MaxPerMemberInput = (props: {
  value: number | null;
  onCommit: (value: number | null) => void;
}): ReactNode => {
  const [text, setText] = useState(
    props.value === null ? "" : String(props.value),
  );
  useEffect(() => {
    setText(props.value === null ? "" : String(props.value));
  }, [props.value]);
  const parsed = parseSquaresMaxPerMember(text);
  const commit = (): void => {
    if (parsed.ok && parsed.value !== props.value) props.onCommit(parsed.value);
  };
  return (
    <Input
      label={SQUARES_MAX_PER_MEMBER_LABEL}
      hint={SQUARES_MAX_PER_MEMBER_HINT}
      type="number"
      inputMode="numeric"
      min={1}
      placeholder={SQUARES_MAX_PER_MEMBER_UNLIMITED}
      value={text}
      data-testid="squares-max-per-member"
      error={parsed.ok ? undefined : SQUARES_MAX_PER_MEMBER_ERROR}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit();
      }}
    />
  );
};

export const SquaresGridRulesFields = (
  props: typeSquaresGridRulesFieldsProps,
): ReactNode => {
  const { presets, value } = props;
  const frozen = new Set(props.frozenFields ?? []);
  const isCustomOffered = props.isCustomAxesOffered ?? true;
  const axesOptions = presets.axesPresets.filter(
    (preset) =>
      preset.gridTypes.includes(value.gridType) &&
      (isCustomOffered ||
        preset.kind !== "custom" ||
        value.axes?.kind === "custom"),
  );
  const alwaysOn = presets.checkpoints
    .filter((checkpoint) => checkpoint.isAlwaysOn)
    .map((checkpoint) => checkpoint.checkpointKey as string);
  const axes = value.axes;
  const teamOnTop =
    axes?.kind === "teamOpponent" &&
    axes.teamId !== null &&
    axes.teamId !== undefined
      ? (props.teamsById.get(axes.teamId)?.name ?? null)
      : null;

  return (
    <div className="flex flex-col gap-6" data-testid="squares-grid-rules">
      <section className="flex flex-col gap-2">
        <h3 className="text-body-strong text-ink">
          {SQUARES_SETUP_AXES_HEADING}
        </h3>
        <p className="text-caption text-ink-secondary">
          {SQUARES_SETUP_AXES_HINT}
        </p>
        <SquaresChipGroup
          mode="single"
          label={SQUARES_SETUP_AXES_HEADING}
          testId="squares-axes-chips"
          value={axes?.kind ?? null}
          options={axesOptions.map((preset) => ({
            value: preset.kind,
            label: preset.label,
            disabled: frozen.has("axes"),
          }))}
          onChange={(kind) => {
            props.onChangeAxes({ kind: kind as typeSquaresAxesKind });
          }}
        />
        {teamOnTop === null ? null : (
          <p className="text-caption text-ink-secondary">
            {SQUARES_AXES_TEAM_PICKED(teamOnTop)}
          </p>
        )}
        {axes?.kind === "custom" ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={SQUARES_AXES_CUSTOM_TOP}
                value={axes.topLabel ?? ""}
                maxLength={24}
                disabled={frozen.has("axes")}
                data-testid="squares-axes-top"
                onChange={(event) => {
                  props.onChangeAxes({ ...axes, topLabel: event.target.value });
                }}
              />
              <Input
                label={SQUARES_AXES_CUSTOM_SIDE}
                value={axes.sideLabel ?? ""}
                maxLength={24}
                disabled={frozen.has("axes")}
                data-testid="squares-axes-side"
                onChange={(event) => {
                  props.onChangeAxes({
                    ...axes,
                    sideLabel: event.target.value,
                  });
                }}
              />
            </div>
            <p className="text-caption text-ink-secondary">
              {SQUARES_AXES_CUSTOM_HINT}
            </p>
          </div>
        ) : null}
        {frozen.has("axes") ? <FrozenNote /> : null}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-body-strong text-ink">
          {SQUARES_SETUP_CHECKPOINTS_HEADING}
        </h3>
        <p className="text-caption text-ink-secondary">
          {SQUARES_SETUP_CHECKPOINTS_HINT}
        </p>
        <SquaresChipGroup
          mode="multi"
          label={SQUARES_SETUP_CHECKPOINTS_HEADING}
          testId="squares-checkpoint-chips"
          value={value.checkpoints}
          lockedValues={alwaysOn}
          options={presets.checkpoints.map((checkpoint) => ({
            value: checkpoint.checkpointKey,
            label: checkpoint.label,
            disabled: frozen.has("checkpoints"),
          }))}
          onChange={(next) => {
            props.onChangeCheckpoints([...new Set([...alwaysOn, ...next])]);
          }}
        />
        {frozen.has("checkpoints") ? <FrozenNote /> : null}
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-body-strong text-ink">
          {SQUARES_SETUP_GRID_SETTINGS_HEADING}
        </h3>
        <Select
          label={SQUARES_GRID_SIZE_LABEL}
          value={String(value.gridSettings.size)}
          disabled={frozen.has("size")}
          hint={
            frozen.has("size")
              ? SQUARES_SETTINGS_LOCKED_AFTER_FIRST_LOCK
              : value.gridSettings.size < 100
                ? SQUARES_GRID_SIZE_HINT_SMALL
                : undefined
          }
          items={presets.gridSizes.map((size) => ({
            value: String(size),
            label: SQUARES_GRID_SIZE_OPTION(size),
          }))}
          onValueChange={(next) => {
            props.onChangeGridSettings({
              ...value.gridSettings,
              size: Number(next),
            });
          }}
        />
        <MaxPerMemberInput
          value={value.gridSettings.maxPerMember ?? null}
          onCommit={(maxPerMember) => {
            props.onChangeGridSettings({ ...value.gridSettings, maxPerMember });
          }}
        />
        <div className="flex flex-col gap-1">
          <Switch
            label={squaresAutoLockLabel({
              gridType: value.gridType,
              leagueKey: presets.leagueKey,
            })}
            description={squaresAutoLockDescription({
              gridType: value.gridType,
              leagueKey: presets.leagueKey,
            })}
            checked={value.gridSettings.autoLock}
            onCheckedChange={(autoLock) => {
              props.onChangeGridSettings({ ...value.gridSettings, autoLock });
            }}
          />
          {props.autoLockNote === null ||
          props.autoLockNote === undefined ||
          !value.gridSettings.autoLock ? null : (
            <p
              className="text-caption text-ink-secondary"
              data-testid="squares-auto-lock-note"
            >
              {props.autoLockNote}
            </p>
          )}
        </div>
        <Switch
          label={SQUARES_SHOW_NUMBERS_LABEL}
          description={SQUARES_SHOW_NUMBERS_DESCRIPTION}
          checked={value.gridSettings.showNumbers}
          onCheckedChange={(showNumbers) => {
            props.onChangeGridSettings({ ...value.gridSettings, showNumbers });
          }}
        />
      </section>

      {props.isNewGridShown ? (
        <section
          className="flex flex-col gap-3"
          data-testid="squares-new-grid-rules"
        >
          <h3 className="text-body-strong text-ink">
            {SQUARES_SETUP_NEW_GRID_HEADING}
          </h3>
          <Switch
            label={SQUARES_CARRY_SQUARES_LABEL}
            description={SQUARES_CARRY_SQUARES_DESCRIPTION}
            checked={value.newGrid.carrySquares}
            onCheckedChange={(carrySquares) => {
              props.onChangeNewGrid({ ...value.newGrid, carrySquares });
            }}
          />
          <Switch
            label={SQUARES_REDRAW_NUMBERS_LABEL}
            description={SQUARES_REDRAW_NUMBERS_DESCRIPTION}
            checked={value.newGrid.redrawNumbers}
            onCheckedChange={(redrawNumbers) => {
              props.onChangeNewGrid({ ...value.newGrid, redrawNumbers });
            }}
          />
          <Switch
            label={SQUARES_AUTO_PUBLISH_LABEL}
            description={SQUARES_AUTO_PUBLISH_DESCRIPTION}
            checked={value.newGrid.autoPublish}
            onCheckedChange={(autoPublish) => {
              props.onChangeNewGrid({ ...value.newGrid, autoPublish });
            }}
          />
        </section>
      ) : null}
    </div>
  );
};
